
import { Language } from '@/services/interfaces/Translation';

// 常用语言列表 (使用实际存在的语言)
 export const popularLanguages = [
    Language.English, //eng
    Language.MandarinChinese, //cmn
    Language.Cantonese, //yue
    Language.Spanish, //spa
    Language.French, //fra
    Language.German, //deu
    Language.Japanese, //jpn
    Language.Korean, //kor
    Language.Portuguese, //por
    Language.Italian, //ita
    Language.Dutch, //nld
    Language.StandardArabic, //arb
    Language.Indonesian //ind
  ];

/**
 * UI Language codes for the application interface
 * These are different from translation Language enum which is for translation services
 */
export enum UILanguage {
  ZH_CN = 'zh-CN', // 简体中文
  EN_US = 'en-US', // English (US)
  JA_JP = 'ja-JP', // 日本語
  // KO_KR = 'ko-KR', // 한국어
  // ES_ES = 'es-ES', // Español
  // FR_FR = 'fr-FR', // Français
  // DE_DE = 'de-DE', // Deutsch
  // RU_RU = 'ru-RU', // Русский
  // PT_BR = 'pt-BR', // Português (Brasil)
  // IT_IT = 'it-IT', // Italiano
}

/**
 * UI Language display information
 */
export interface UILanguageInfo {
  code: UILanguage;
  name: string;
  nativeName: string;
  flag: string;
}

/**
 * Available UI languages with their display information
 */
export const UI_LANGUAGES: Record<UILanguage, UILanguageInfo> = {
  [UILanguage.ZH_CN]: {
    code: UILanguage.ZH_CN,
    name: 'Chinese (Simplified)',
    nativeName: '简体中文',
    flag: '🇨🇳'
  },
  [UILanguage.EN_US]: {
    code: UILanguage.EN_US,
    name: 'English (US)',
    nativeName: 'English',
    flag: '🇺🇸'
  },
  [UILanguage.JA_JP]: {
    code: UILanguage.JA_JP,
    name: 'Japanese',
    nativeName: '日本語',
    flag: '🇯🇵'
  },
  // [UILanguage.KO_KR]: {
  //   code: UILanguage.KO_KR,
  //   name: 'Korean',
  //   nativeName: '한국어',
  //   flag: '🇰🇷'
  // },
  // [UILanguage.ES_ES]: {
  //   code: UILanguage.ES_ES,
  //   name: 'Spanish',
  //   nativeName: 'Español',
  //   flag: '🇪🇸'
  // },
  // [UILanguage.FR_FR]: {
  //   code: UILanguage.FR_FR,
  //   name: 'French',
  //   nativeName: 'Français',
  //   flag: '🇫🇷'
  // },
  // [UILanguage.DE_DE]: {
  //   code: UILanguage.DE_DE,
  //   name: 'German',
  //   nativeName: 'Deutsch',
  //   flag: '🇩🇪'
  // },
  // [UILanguage.RU_RU]: {
  //   code: UILanguage.RU_RU,
  //   name: 'Russian',
  //   nativeName: 'Русский',
  //   flag: '🇷🇺'
  // },
  // [UILanguage.PT_BR]: {
  //   code: UILanguage.PT_BR,
  //   name: 'Portuguese (Brazil)',
  //   nativeName: 'Português (Brasil)',
  //   flag: '🇧🇷'
  // },
  // [UILanguage.IT_IT]: {
  //   code: UILanguage.IT_IT,
  //   name: 'Italian',
  //   nativeName: 'Italiano',
  //   flag: '🇮🇹'
  // }
};

/**
 * Translation key structure for type safety
 */
export interface TranslationKeys {
  language: {
    cmn: string; // 简体中文
    eng: string; // English
    jpn: string; // Japanese
    kor: string; // Korean
    spa: string; // Spanish
    fra: string; // French
    deu: string; // German
    rus: string; // Russian
    por: string; // Portuguese
    ita: string; // Italian
    yue: string; // 粤语
    nld: string; // Dutch
    arb: string; // Standard Arabic
    ind: string; // Indonesian
  };

  // Common
  common: {
    loading: string;
    error: string;
    success: string;
    cancel: string;
    confirm: string;
    save: string;
    delete: string;
    edit: string;
    close: string;
    back: string;
    next: string;
    previous: string;
    retry: string;
    refresh: string;
    saving: string;
    processing: string;
    understood: string;
    popular: string;
    tokenNotEnough: string;
  };

  // Navigation
  nav: {
    home: string;
    translation: string;
    history: string;
    settings: string;
    help: string;
    about: string;
    profile: string;
    logout: string;
  };

  // Translation features
  translation: {
    startTranslation: string;
    stopTranslation: string;
    stopping: string;
    realTimeTranslation: string;
    sourceLanguage: string;
    targetLanguage: string;
    swapLanguages: string;
    selectLanguage: string;
    selectLanguageDescription: string;
    detectLanguage: string;
    translating: string;
    translationComplete: string;
    translationError: string;
    noTranslationYet: string;
    speakNow: string;
    listening: string;
    processing: string;
    sameLanguageError: string;
  };

  // Audio/Microphone
  audio: {
    microphonePermission: string;
    microphonePermissionDenied: string;
    selectMicrophone: string;
    noMicrophoneFound: string;
    microphoneError: string;
    requestPermission: string;
    permissionGranted: string;
    permissionRequired: string;
  };

  // Settings
  settings: {
    title: string;
    language: string;
    uiLanguage: string;
    theme: string;
    audio: string;
    account: string;
    privacy: string;
    about: string;
    version: string;
    description: string;
    profile: string;
    security: string;
    dangerZone: string;
    pleaseLogin: string;
  };

  // Profile settings
  profile: {
    displayName: string;
    email: string;
    emailNotifications: string;
    emailNotificationsDesc: string;
    memberSince: string;
    enterName: string;
    nameRequired: string;
    updateSuccess: string;
    updateFailed: string;
    avatarUploadComingSoon: string;
  };

  // Security settings
  security: {
    twoFactorAuth: string;
    mfaDescription: string;
    enableMfa: string;
    disable: string;
    enable: string;
    enabled: string;
    mfaEnabled: string;
    mfaDisabled: string;
    mfaUpdateFailed: string;
    socialLoginMfaWarning: string;
    socialLoginMfaWarningDesc: string;
    password: string;
    changePassword: string;
    passwordDescription: string;
    currentPassword: string;
    newPassword: string;
    confirmPassword: string;
    updatePassword: string;
    passwordChanged: string;
    passwordChangeFailed: string;
    passwordMismatch: string;
    passwordTooShort: string;
    loginHistory: string;
    lastLogin: string;
    never: string;
    accountCreated: string;
    loginMethod: string;
    socialLogin: string;
    socialLoginConnected: string;
    socialLoginNote: string;
    socialLoginNoteDesc: string;
    mfaNotAvailable: string;
    mfaNotAvailableTitle: string;
    mfaNotAvailableDesc: string;
    mfaModalDescription: string;
    mfaWillProvide: string;
    mfaBenefit1: string;
    mfaBenefit2: string;
    mfaBenefit3: string;
    mfaWarning: string;
    logoutAllDevices: string;
    logoutAllDevicesDesc: string;
    logout: string;
    delete:string;
    deleteAccount: string;
    deleteAccountDesc: string;
    confirmLogout: string;
    confirmLogoutDesc: string;
    confirmDelete: string;
    confirmDeleteDesc: string;
    deleteWarning: string;
    deleteWarning1: string;
    deleteWarning2: string;
    deleteWarning3: string;
    deleteConfirmLabel: string;
    logoutSuccess: string;
    logoutFailed: string;
    accountDeleted: string;
    accountDeleteFailed: string;
    deleteConfirmError: string;
  };

  // Errors
  errors: {
    networkError: string;
    serviceUnavailable: string;
    translationFailed: string;
    audioError: string;
    permissionError: string;
    unknownError: string;
    tryAgain: string;
  };

  // Home page
  home: {
    title: string;
    subtitle: string;
    description: string;
    getStarted: string;
    features: {
      realTime: string;
      multiLanguage: string;
      highQuality: string;
    };
  };

  // Free trial
  freeTrial: {
    title: string;
    description: string;
    tokensRemaining: string;
    timeRemaining: string;
    upgradeNow: string;
    trialExpired: string;
    trialEnded: string;
    trialUnavailable: string;
    getOfficialAccount: string;
    loginForMore: string;
    expired: string;
    tokensExhausted: string;
    notEligible: string;
    errorMessage: string;
    reason: string;
    solution: string;
    registerLogin: string;
    immediateRegister: string;
    conversationsNotSaved: string;
    experienceOnly: string;
    loginForPermanentSave: string;
    hours: string;
    minutes: string;
    unlimited: string;
    freeQuota: string;
    tokens: string;
    pro: string;

    // Banner specific texts
    betaStage: string;
    betaInProgress: string;
    productInBeta: string;
    joinWaitlist: string;
    joinWaitlistFull: string;
    welcomeJoinWaitlist: string;
    freeTrialActive: string;
    freeTrialNotAvailable: string;
    trialEndedShort: string;
    freeTrialEndedFull: string;
    tokensUsedUp: string;
    tokensUsedUpDesc: string;
    trialExpiredDesc: string;
    registerForMore: string;
    registerForMoreDesc: string;
    fullFeatureExperience: string;
    fullFeatureExperienceDesc: string;
    remainingTokens: string;
    remainingTime: string;
    freeTrialNotice: string;
    conversationsNotSavedDesc: string;
    experienceOnlyDesc: string;
    loginForPermanentSaveDesc: string;
    deviceUsedTrialExpired: string;
    deviceUsedTrialExhausted: string;
  };

  // Authentication
  auth: {
    // 通用
    login: string;
    logout: string;
    register: string;
    email: string;
    password: string;
    callback: string;
    forgotPassword: string;
    rememberMe: string;
    loginSuccess: string;
    loginError: string;
    logoutSuccess: string;

    // 登录表单
    welcomeBack: string;
    loginWithSocialOrEmail: string;
    emailPlaceholder: string;
    forgotPasswordLink: string;
    loggingIn: string;
    dontHaveAccount: string;
    signUp: string;

    // 注册表单
    createAccount: string;
    verifyYourEmail: string;
    signUpWithSocialOrEmail: string;
    enterVerificationCode: string;
    fullName: string;
    fullNamePlaceholder: string;
    passwordPlaceholder: string;
    confirmPassword: string;
    confirmPasswordPlaceholder: string;
    invitationCode: string;
    invitationCodeRequired: string;
    invitationCodePlaceholder: string;
    invitationCodeDescription: string;
    verificationCode: string;
    verificationCodePlaceholder: string;
    codeSentTo: string;
    resendVerificationCode: string;
    sendVerificationCode: string;
    createAccountButton: string;
    sendingCode: string;
    creatingAccount: string;
    alreadyHaveAccount: string;
    signIn: string;
    backToPreviousStep: string;

    // 忘记密码表单
    resetPassword: string;
    setNewPassword: string;
    enterEmailForReset: string;
    newPassword: string;
    newPasswordPlaceholder: string;
    confirmNewPassword: string;
    confirmNewPasswordPlaceholder: string;
    sendingCodeLoading: string;
    verifying: string;
    resettingPassword: string;
    verifyCode: string;
    resetPasswordButton: string;
    rememberPassword: string;
    backToLogin: string;

    // MFA 双因素认证
    verificationCodeMFA: string;
    sendCode: string;
    sending: string;
    resendCode: string;
    clickSendCodeDescription: string;

    // 社交登录
    orContinueWith: string;
    orContinueWithEmail: string;
    apple: string;
    google: string;

    // 错误消息
    pleaseEnterEmail: string;
    pleaseEnterEmailFirst: string;
    pleaseEnterVerificationCode: string;
    passwordsDoNotMatch: string;
    passwordTooShort: string;
    invitationCodeRequiredError: string;
    failedToSendCode: string;
    invalidVerificationCode: string;
    passwordResetFailed: string;
    registrationFailed: string;
    mfaRequired: string;
    userDoesNotExist: string;
    invalidCredentials: string;

    // 成功消息
    verificationCodeSent: string;
    passwordResetCodeSent: string;
    codeVerified: string;
    passwordResetSuccessful: string;
    registrationSuccessful: string;
    mfaCodeSent: string;

    // 法律条款
    termsAndPrivacy: string;
    termsOfService: string;
    and: string;
    privacyPolicy: string;
  };

  // Chat/Conversation
  chat: {
    conversations: string;
    noConversations: string;
    loadingConversations: string;
    conversationHistory: string;
    backToList: string;
    totalTurns: string;
    loadedTurns: string;
    noContent: string;
    loadingConversation: string;
    sessionTitle: string;
    createdAt: string;
    loadMore: string;
    remainingTurns: string;
    allLoaded: string;
    allLoadedMessage: string;
    browseHistory: string;
    totalSessions: string;
    startNewConversation: string;
    deleteSession: string;
    confirmDeleteSession: string;
    deleteSessionWarning: string;
    confirmDelete: string;
    turnsCount: string;
    preview: string;
  };

  // Purchase/Payment
  purchase: {
    title: string;
    buyTokens: string;
    orderHistory: string;
    noOrders: string;
    loadingOrders: string;
    paymentMethod: string;
    amount: string;
    status: string;
    date: string;
    processing: string;
    completed: string;
    failed: string;
    subscriptionPlans: string;
    subscriptionDescription: string;
    subscribeNow: string;
    tokenPacks: string;
    tokenPacksDescription: string;
    buyNow: string;
    oneTimePayment: string;
    popular: string;
    recommended: string;
    perMonth: string;
    tokens: string;
    features: string;
    tokensIncluded: string;
    noExpirationDate: string;
    payOnceUseAnytime: string;
    perfectForOccasionalUsers: string;
    selectPaymentMethod: string;
    selectPaymentDescription: string;
    alipayName: string;
    alipayDescription: string;
    polarName: string;
    polarDescription: string;
    creatingOrder: string;
    cancel: string;
    scanToPay: string;
    completePayment: string;
    alipayPaymentDescription: string;
    polarPaymentDescription: string;
    paymentWindowOpened: string;
    completePaymentInNewWindow: string;
    openInBrowser: string;
    reopenPaymentWindow: string;
    checkingStatus: string;
    checkPaymentStatus: string;
    close: string;
    orderId: string;
    waitingForPayment: string;
    paymentReceived: string;
    paymentSuccessful: string;
    paymentFailed: string;
    paymentExpired: string;
  };

  // Admin
  admin: {
    title: string;
  };

  // Debug/Development
  debug: {
    debugPanel: string;
    testEvents: string;
    parseEvents: string;
    clearEvents: string;
    clearAll: string;
    eventData: string;
    parseError: string;
    testAndParse: string;
    inputPlaceholder: string;
  };

  // UI Components
  ui: {
    swap: string;
    siderbarTitle: string;
    selectLanguageDescription: string;
    translationLanguageSettings: string;
    comingSoon: string;
    audioSettingsDescription: string;
    accountSettingsDescription: string;
    microphoneAccessDenied: string;
    microphoneInstructions: string;
    refreshPage: string;
    openSettings: string;
    dismiss: string;
    microphonePermissionSteps: string;
    chromeInstructions: string;
    firefoxInstructions: string;
    safariInstructions: string;
    genericInstructions: string;
    settingsCompleteRefresh: string;
    iosPermissionTitle: string;
    iosPermissionDescription: string;
    iosPermissionTip: string;
    iosHelp: string;
    retryPermission: string;
    openSystemSettings: string;
  };

  // Conversation
  conversation: {
    noContent: string;
    startSpeaking: string;
    autoTranslate: string;
    turnsCount: string;
    tokens: string;
    totalCost: string;
    translation: string;
    userA: string;
    userB: string;
    cancelled: string;
    translationIncomplete: string;
  };
}

/**
 * Default UI language - will be set by detectBrowserLanguage() in index.ts
 * This is a placeholder that will be overridden by the actual detection logic
 */
export const DEFAULT_UI_LANGUAGE = UILanguage.EN_US;

/**
 * Local storage key for UI language preference
 */
export const UI_LANGUAGE_STORAGE_KEY = 'translator-ui-language';
